import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Warning } from '@/api/base-types';
import { useBaseMutation, useClientCheck, useUsers } from '@/api/base';
import { api } from '@/api/client';
import { Button } from '@/components/kit/Button';
import { TextLink } from '@/components/kit/Controls';
import { DateInput } from '@/components/kit/DateInput';
import { PhoneField, TextField } from '@/components/kit/Field';
import { Breadcrumbs, Kv, PageHeader } from '@/components/kit/Layout';
import { Note } from '@/components/kit/Note';
import { Panel } from '@/components/kit/Panel';
import { SelectField } from '@/components/kit/Popover';
import { useToast } from '@/components/kit/Toast';
import { CityField, type CityValue } from '../shared/CityField';
import { errorText } from '../shared/useUndoable';

/**
 * Новый клиент — экран, а не диалог (viewNew в макете). Минимум —
 * фамилия и имя (БЛ 3.1); остальное потребуется, когда клиент получит
 * роль. Занятый номер и похожие карточки видны сразу, до сохранения.
 * Ответственный — здесь же: экран, не окно, поповер можно (ТЗ §11 п.1).
 */
export function NewClientPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const users = useUsers();
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState<string | null>(null);
  const [birthValid, setBirthValid] = useState(true);
  const [email, setEmail] = useState('');
  const [telegram, setTelegram] = useState('');
  const [instagram, setInstagram] = useState('');
  const [city, setCity] = useState<CityValue | null>(null);
  const [responsible, setResponsible] = useState<string | null>(null);

  const [debounced, setDebounced] = useState({ phone: '', lastName: '', firstName: '' });
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced({ phone, lastName, firstName }), 300);
    return () => window.clearTimeout(t);
  }, [phone, lastName, firstName]);
  const check = useClientCheck({ ...debounced, birthDate });
  const taken = phone.trim() ? check.data?.phoneTaken : null;
  const invalid = phone.trim() && check.data?.phoneInvalid;
  const twin = check.data?.twins[0];

  const create = useBaseMutation(() =>
    api.post<{ id: string; warnings: Warning[] }>('/clients', {
      lastName,
      firstName,
      middleName: middleName || null,
      phone: phone.trim() || null,
      birthDate,
      email: email.trim() || null,
      // «@ник» и ссылки t.me / instagram.com сервер приводит к нику
      telegramUsername: telegram.trim() || null,
      instagram: instagram.trim() || null,
      cityId: city?.id ?? null,
      responsibleUserId: responsible,
    }),
  );

  const ready = lastName.trim() && firstName.trim() && birthValid && !taken && !create.isPending;

  return (
    <div className="main">
      <Breadcrumbs items={[{ label: 'Клиенты', onClick: () => navigate('/clients') }, { label: 'Новый клиент' }]} />
      <PageHeader title="Новый клиент" />
      <div className="form">
        <Panel>
          <div className="row">
            <div className="col">
              <TextField label="Фамилия" value={lastName} onValue={setLastName} autoFocus />
            </div>
            <div className="col">
              <TextField label="Имя" value={firstName} onValue={setFirstName} />
            </div>
            <div className="col">
              <TextField label="Отчество" value={middleName} onValue={setMiddleName} />
            </div>
          </div>
          <div className="row">
            <div className="col">
              <PhoneField
                label="Телефон"
                value={phone}
                onValue={setPhone}
                error={invalid ? 'Неполный номер' : null}
              />
            </div>
            <div className="col">
              <DateInput
                label="Дата рождения"
                value={birthDate}
                onChange={(v, ok) => {
                  setBirthDate(v);
                  setBirthValid(ok);
                }}
              />
            </div>
          </div>
          <div className="row">
            <div className="col">
              <TextField label="Почта" inputMode="email" value={email} onValue={setEmail} />
            </div>
            <div className="col">
              <CityField value={city} onChange={setCity} />
            </div>
          </div>
          <div className="row">
            <div className="col">
              <TextField
                label="Telegram"
                placeholder="ник без @, пока бот не подключён"
                value={telegram}
                onValue={setTelegram}
              />
            </div>
            <div className="col">
              <TextField label="Instagram" placeholder="ник без @" value={instagram} onValue={setInstagram} />
            </div>
          </div>
          <div className="row">
            <div className="col">
              <SelectField
                label="Ответственный"
                value={responsible}
                placeholder="не назначен"
                options={(users.data ?? []).map((u) => ({ value: u.id, label: u.fullName }))}
                onChange={setResponsible}
                hint="обязателен руководителю команды, остальным — по желанию"
              />
            </div>
            <div className="col" />
          </div>

          <div className="ndup">
            {taken ? (
              <Note tone="warn">
                <b>Номер уже у клиента «{taken.fullName}».</b> Второго клиента с этим номером завести нельзя.{' '}
                <TextLink onClick={() => navigate(`/clients/${taken.id}`)}>Открыть его</TextLink>
              </Note>
            ) : twin ? (
              <Note tone="warn">
                <b>Похоже на дубль:</b> уже есть «{twin.fullName}» с той же датой рождения
                {twin.archived ? ' (в архиве)' : ''}. Создать можно, но проверьте.{' '}
                <TextLink onClick={() => navigate(`/clients/${twin.id}`)}>Открыть</TextLink>
              </Note>
            ) : null}
          </div>

          <div className="blk reqs">
            <p className="sec">Что обязательно</p>
            <Kv k="Чтобы создать">фамилия и имя</Kv>
            <Kv k="Руководителю команды, контакту студии, представителю">телефон</Kv>
            <Kv k="Участнику">дата рождения</Kv>
            <p className="txt">
              Остальное можно дополнить позже — система попросит недостающее, когда клиент получит роль.
            </p>
          </div>

          <div className="fbtns">
            <Button
              tone="primary"
              disabled={!ready}
              onClick={() =>
                create.mutate(undefined, {
                  onSuccess: (r) => {
                    toast.show('Клиент создан');
                    navigate(`/clients/${r.id}`);
                  },
                  onError: (e) => toast.showError(errorText(e)),
                })
              }
            >
              Создать клиента
            </Button>
            <Button onClick={() => navigate('/clients')}>Отмена</Button>
          </div>
        </Panel>
      </div>
    </div>
  );
}
